import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Tooltip,
  notification,
  Drawer,
  Descriptions,
  Typography,
  message,
  Spin,
  Empty,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  SendOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import lib from '../../../assets/utils/lib';
import {
  SAASPRO_OUTBOX_STATUS_COLORS,
  SAASPRO_DECISION_STATUS_COLORS,
  formatConfidence,
  formatCurrencyAmount,
  recommendationColor,
  recommendationLabel,
  truncateMiddle,
} from '../../../components/SaaSPro/SaaSProAdvisoryShared';

const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const STREAM_OPTIONS = [
  { value: 'all', label: 'All activity' },
  { value: 'outbox', label: 'Outbound (Aman → partner)' },
  { value: 'decision', label: 'Inbound (partner → Aman)' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  {
    label: 'Outbound',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'sent', label: 'Sent' },
      { value: 'failed', label: 'Failed' },
      { value: 'shadow', label: 'Shadow' },
    ],
  },
  {
    label: 'Inbound',
    options: [
      { value: 'advisory', label: 'Advisory' },
      { value: 'applied', label: 'Applied' },
      { value: 'superseded', label: 'Superseded' },
      { value: 'rejected_by_human', label: 'Rejected by human' },
    ],
  },
];

const RECOMMENDATION_OPTIONS = [
  { value: 'all', label: 'All recommendations' },
  { value: 'approve', label: 'Approve' },
  { value: 'partial_approve', label: 'Partial approve' },
  { value: 'query', label: 'Query' },
  { value: 'reject', label: 'Reject' },
];

const EVENT_TYPE_OPTIONS = [
  { value: 'all', label: 'All event types' },
  { value: 'pa.submitted', label: 'pa.submitted' },
  { value: 'pa.approved', label: 'pa.approved' },
  { value: 'pa.rejected', label: 'pa.rejected' },
  { value: 'pa.queried', label: 'pa.queried' },
  { value: 'pa.flagged', label: 'pa.flagged' },
  { value: 'pa.unflagged', label: 'pa.unflagged' },
  { value: 'pa.decided', label: 'pa.decided' },
];

const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const copyToClipboard = async (value) => {
  if (!value) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(value));
    } else {
      const ta = document.createElement('textarea');
      ta.value = String(value);
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    message.success('Copied');
  } catch (e) {
    message.error('Copy failed');
  }
};

const parseListResponse = (resp) => {
  const body = resp?.data || resp;
  const data = body?.data || body;
  const list = Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data)
      ? data
      : [];
  const pag = body?.pagination || data?.pagination || {};
  const total = Number(pag.total ?? data?.total ?? list.length) || 0;
  return { list, total };
};

const emptyFilters = (initial) => {
  const src = initial || {};
  return {
    q: src.q || '',
    stream: src.stream || 'all',
    status: src.status || 'all',
    recommendation: src.recommendation || 'all',
    event_type: src.event_type || 'all',
    checkin_id: src.checkin_id || '',
    correlation_id: src.correlation_id || '',
    claim_item_id: src.claim_item_id || '',
    range: null,
  };
};

const ActivityTab = ({ partnerId, initialFilters, onJumpToDecisions }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => emptyFilters(initialFilters));
  const [applied, setApplied] = useState(() => emptyFilters(initialFilters));
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [redispatching, setRedispatching] = useState(false);

  useEffect(() => {
    if (!initialFilters) return;
    setFilters((f) => ({
      ...f,
      q: initialFilters.q ?? f.q,
      checkin_id: initialFilters.checkin_id ?? f.checkin_id,
      correlation_id: initialFilters.correlation_id ?? f.correlation_id,
      claim_item_id: initialFilters.claim_item_id ?? f.claim_item_id,
    }));
    setApplied((f) => ({
      ...f,
      q: initialFilters.q ?? f.q,
      checkin_id: initialFilters.checkin_id ?? f.checkin_id,
      correlation_id: initialFilters.correlation_id ?? f.correlation_id,
      claim_item_id: initialFilters.claim_item_id ?? f.claim_item_id,
    }));
    setPagination((p) => ({ ...p, page: 1 }));
  }, [initialFilters]);

  const buildQuery = useCallback(() => {
    const q = {
      page: pagination.page,
      pageSize: pagination.pageSize,
    };
    if (applied.q?.trim()) q.q = applied.q.trim();
    if (applied.stream && applied.stream !== 'all') q.stream = applied.stream;
    if (applied.status && applied.status !== 'all') q.status = applied.status;
    if (applied.recommendation && applied.recommendation !== 'all') {
      q.recommendation = applied.recommendation;
    }
    if (applied.event_type && applied.event_type !== 'all') q.event_type = applied.event_type;
    if (applied.checkin_id?.trim()) q.checkin_id = applied.checkin_id.trim();
    if (applied.correlation_id?.trim()) q.correlation_id = applied.correlation_id.trim();
    if (applied.claim_item_id?.trim()) q.claim_item_id = applied.claim_item_id.trim();
    if (applied.range && applied.range[0]) q.from = applied.range[0].toISOString();
    if (applied.range && applied.range[1]) q.to = applied.range[1].toISOString();
    return q;
  }, [applied, pagination.page, pagination.pageSize]);

  const fetchRows = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const resp = await lib.searchPaAgentActivity(partnerId, buildQuery());
      const body = resp?.data || resp;
      if (Number(body?.error) || resp?.status === 'error') {
        notification.error({ message: body?.message || 'Failed to load activity' });
        setRows([]);
        setPagination((p) => ({ ...p, total: 0 }));
        return;
      }
      const { list, total } = parseListResponse(resp);
      setRows(list);
      setPagination((p) => ({ ...p, total }));
    } catch (e) {
      notification.error({ message: 'Failed to load activity', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [buildQuery, partnerId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const applyFilters = useCallback(() => {
    setApplied({ ...filters });
    setPagination((p) => ({ ...p, page: 1 }));
  }, [filters]);

  const resetFilters = useCallback(() => {
    const next = emptyFilters();
    setFilters(next);
    setApplied(next);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const openDetail = useCallback(async (row) => {
    if (!row) return;
    const stream = row.stream === 'decision' ? 'decision' : 'outbox';
    setActive({ stream, id: row.id, event_id: row.event_id });
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = stream === 'decision'
        ? await lib.getPaAgentDecisionDetail(partnerId, row.id)
        : await lib.getPaAgentOutboxDetail(partnerId, row.event_id);
      const body = resp?.data || resp;
      if (Number(body?.error) || resp?.status === 'error') {
        notification.error({ message: body?.message || 'Could not load detail' });
        setActive(null);
        return;
      }
      setDetail(body?.data || body);
    } catch (e) {
      notification.error({ message: 'Could not load detail', description: e?.message });
      setActive(null);
    } finally {
      setDetailLoading(false);
    }
  }, [partnerId]);

  const closeDetail = useCallback(() => {
    setActive(null);
    setDetail(null);
  }, []);

  const handleRedispatch = useCallback(async () => {
    if (!active?.event_id || active.stream !== 'outbox') return;
    setRedispatching(true);
    try {
      const resp = await lib.redispatchPaAgentEvent(partnerId, active.event_id);
      const body = resp?.data || resp;
      if (Number(body?.error) || resp?.status === 'error') {
        notification.error({ message: body?.message || 'Redispatch failed' });
        return;
      }
      notification.success({ message: 'Redispatch queued' });
      await fetchRows();
      await openDetail({ ...active, stream: 'outbox', event_id: active.event_id, id: active.id });
    } catch (e) {
      notification.error({ message: 'Redispatch failed', description: e?.message });
    } finally {
      setRedispatching(false);
    }
  }, [active, partnerId, fetchRows, openDetail]);

  const columns = useMemo(() => [
    {
      title: 'When',
      dataIndex: 'occurred_at',
      width: 170,
      render: (v) => <span style={{ fontSize: 12 }}>{fmtDateTime(v)}</span>,
    },
    {
      title: 'Stream',
      dataIndex: 'stream',
      width: 110,
      render: (v) => (
        <Tag color={v === 'decision' ? 'cyan' : 'purple'} style={{ marginInlineEnd: 0 }}>
          {v === 'decision' ? 'Inbound' : 'Outbound'}
        </Tag>
      ),
    },
    {
      title: 'Checkin',
      dataIndex: 'checkin_id',
      width: 200,
      render: (v) => v ? (
        <Button
          type="link"
          size="small"
          icon={<LinkOutlined />}
          style={{ padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/preauth-action?checkin=${encodeURIComponent(v)}`, { state: { checkinId: v } });
          }}
        >
          {v}
        </Button>
      ) : '--',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (v, row) => {
        const colors = row.stream === 'decision'
          ? SAASPRO_DECISION_STATUS_COLORS
          : SAASPRO_OUTBOX_STATUS_COLORS;
        return (
          <Tag color={colors[v] || 'default'} style={{ marginInlineEnd: 0 }}>
            {v || '--'}
          </Tag>
        );
      },
    },
    {
      title: 'Kind',
      key: 'kind',
      width: 160,
      render: (_, row) => {
        if (row.stream === 'decision') {
          return (
            <Tag color={recommendationColor(row.recommendation)} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
              {recommendationLabel(row.recommendation)}
            </Tag>
          );
        }
        return <code style={{ fontSize: 12 }}>{row.event_type || '--'}</code>;
      },
    },
    {
      title: 'Item',
      dataIndex: 'claim_item_id',
      width: 80,
      render: (v) => v ?? '--',
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      ellipsis: true,
      render: (v) => v ? (
        <Tooltip title={v}>
          <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>{v}</span>
        </Tooltip>
      ) : null,
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      render: (_, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={(e) => { e.stopPropagation(); openDetail(row); }}
        />
      ),
    },
  ], [navigate, openDetail]);

  const isDecision = active?.stream === 'decision';
  const payloadString = useMemo(() => {
    const raw = isDecision ? detail?.raw_payload : detail?.payload;
    if (!raw) return '';
    try {
      return JSON.stringify(raw, null, 2);
    } catch (e) {
      return String(raw);
    }
  }, [detail, isDecision]);

  const citations = useMemo(() => (
    Array.isArray(detail?.policy_citations) ? detail.policy_citations : []
  ), [detail]);

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }} align="start">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search checkin, event id, correlation, item id, rationale, error…"
          style={{ width: 360 }}
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          onPressEnter={applyFilters}
        />
        <Select
          style={{ width: 220 }}
          value={filters.stream}
          onChange={(v) => setFilters((f) => ({ ...f, stream: v }))}
          options={STREAM_OPTIONS}
        />
        <Select
          style={{ width: 180 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={STATUS_OPTIONS}
        />
        <Select
          style={{ width: 180 }}
          value={filters.recommendation}
          onChange={(v) => setFilters((f) => ({ ...f, recommendation: v }))}
          options={RECOMMENDATION_OPTIONS}
        />
        <Select
          style={{ width: 170 }}
          value={filters.event_type}
          onChange={(v) => setFilters((f) => ({ ...f, event_type: v }))}
          options={EVENT_TYPE_OPTIONS}
        />
        <Input
          allowClear
          placeholder="Checkin id"
          style={{ width: 200 }}
          value={filters.checkin_id}
          onChange={(e) => setFilters((f) => ({ ...f, checkin_id: e.target.value }))}
          onPressEnter={applyFilters}
        />
        <Input
          allowClear
          placeholder="Correlation / event id"
          style={{ width: 220 }}
          value={filters.correlation_id}
          onChange={(e) => setFilters((f) => ({ ...f, correlation_id: e.target.value }))}
          onPressEnter={applyFilters}
        />
        <Input
          allowClear
          placeholder="Claim item id"
          style={{ width: 140 }}
          value={filters.claim_item_id}
          onChange={(e) => setFilters((f) => ({ ...f, claim_item_id: e.target.value }))}
          onPressEnter={applyFilters}
        />
        <RangePicker
          showTime
          value={filters.range}
          onChange={(value) => setFilters((f) => ({ ...f, range: value }))}
        />
        <Button type="primary" onClick={applyFilters}>Search</Button>
        <Button onClick={resetFilters}>Reset</Button>
        <Button icon={<ReloadOutlined />} onClick={fetchRows}>Refresh</Button>
      </Space>

      <Table
        size="small"
        rowKey={(row) => `${row.stream}-${row.id}-${row.event_id || ''}-${row.claim_item_id || ''}`}
        columns={columns}
        dataSource={rows}
        loading={loading}
        onRow={(row) => ({ onClick: () => openDetail(row) })}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `${total} event${total === 1 ? '' : 's'}`,
          onChange: (page, pageSize) => setPagination({ ...pagination, page, pageSize }),
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: 'rgba(0,0,0,0.55)' }}>
                  No PA Agent activity matches these filters.
                </span>
              }
            />
          ),
        }}
      />

      <Drawer
        title={detail ? (
          <Space wrap>
            <Tag color={isDecision ? 'cyan' : 'purple'}>
              {isDecision ? 'Inbound decision' : 'Outbound event'}
            </Tag>
            {isDecision ? (
              <Tag color={recommendationColor(detail.recommendation)}>
                {recommendationLabel(detail.recommendation)}
              </Tag>
            ) : (
              <Tag color={SAASPRO_OUTBOX_STATUS_COLORS[detail.status] || 'default'}>
                {detail.status}
              </Tag>
            )}
            <Text>{detail.checkin_id}</Text>
          </Space>
        ) : 'Activity'}
        width={720}
        open={Boolean(active)}
        onClose={closeDetail}
        destroyOnClose
        extra={
          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(isDecision ? detail?.correlation_id : detail?.event_id)}
              disabled={!(isDecision ? detail?.correlation_id : detail?.event_id)}
            >
              Copy id
            </Button>
            <Button
              icon={<LinkOutlined />}
              onClick={() => detail?.checkin_id && navigate(`/preauth-action?checkin=${encodeURIComponent(detail.checkin_id)}`, { state: { checkinId: detail.checkin_id } })}
              disabled={!detail?.checkin_id}
            >
              Encounter
            </Button>
            {!isDecision ? (
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={redispatching}
                onClick={handleRedispatch}
                disabled={!detail}
              >
                Redispatch
              </Button>
            ) : null}
          </Space>
        }
      >
        {detailLoading ? (
          <Spin />
        ) : detail ? (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Checkin id">{detail.checkin_id || '--'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={
                    (isDecision ? SAASPRO_DECISION_STATUS_COLORS : SAASPRO_OUTBOX_STATUS_COLORS)[detail.status]
                    || 'default'
                  }
                >
                  {detail.status || '--'}
                </Tag>
              </Descriptions.Item>
              {isDecision ? (
                <>
                  <Descriptions.Item label="Claim item">{detail.claim_item_id ?? '--'}</Descriptions.Item>
                  <Descriptions.Item label="Recommended cost">
                    {formatCurrencyAmount(detail.recommended_approved_cost, '--')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Confidence">
                    {formatConfidence(detail.confidence) || '--'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Correlation id">
                    <code style={{ fontSize: 12 }}>{detail.correlation_id || '--'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Event id">
                    <code style={{ fontSize: 12 }}>{detail.event_id || '--'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Decided at">{fmtDateTime(detail.decided_at)}</Descriptions.Item>
                  <Descriptions.Item label="Received at">{fmtDateTime(detail.received_at)}</Descriptions.Item>
                  <Descriptions.Item label="Overall">
                    {detail.overall_recommendation ? (
                      <Tag color={recommendationColor(detail.overall_recommendation)}>
                        {recommendationLabel(detail.overall_recommendation)}
                      </Tag>
                    ) : '--'}
                  </Descriptions.Item>
                </>
              ) : (
                <>
                  <Descriptions.Item label="Event id">
                    <code style={{ fontSize: 12 }}>{detail.event_id || '--'}</code>
                  </Descriptions.Item>
                  <Descriptions.Item label="Event type">{detail.event_type || '--'}</Descriptions.Item>
                  <Descriptions.Item label="Revision">{detail.submission_revision ?? '--'}</Descriptions.Item>
                  <Descriptions.Item label="Attempts">{detail.attempts ?? 0}</Descriptions.Item>
                  <Descriptions.Item label="HTTP">{detail.http_status ?? '--'}</Descriptions.Item>
                  <Descriptions.Item label="Sent at">{detail.sent_at ? fmtDateTime(detail.sent_at) : '--'}</Descriptions.Item>
                  <Descriptions.Item label="Last error">
                    {detail.last_error ? <Text type="danger">{detail.last_error}</Text> : '--'}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>

            {isDecision && detail.rationale ? (
              <div style={{ marginTop: 16 }}>
                <Text strong>Rationale</Text>
                <Paragraph
                  style={{
                    background: 'rgba(0,0,0,0.03)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: 6,
                    padding: 10,
                    marginTop: 6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {detail.rationale}
                </Paragraph>
              </div>
            ) : null}

            {isDecision && citations.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                <Text strong>Citations</Text>
                <div style={{ marginTop: 6 }}>
                  {citations.map((cite, idx) => (
                    <Tag key={`${cite}-${idx}`} color="geekblue" style={{ marginBottom: 4 }}>
                      {cite}
                    </Tag>
                  ))}
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: 16 }}>
              <Space style={{ marginBottom: 6 }}>
                <Text strong>{isDecision ? 'Raw payload' : 'Payload'}</Text>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(payloadString)}
                  disabled={!payloadString}
                >
                  Copy JSON
                </Button>
                {onJumpToDecisions && detail.event_id ? (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => onJumpToDecisions({
                      correlation_id: detail.correlation_id || detail.event_id,
                      checkin_id: detail.checkin_id,
                    })}
                  >
                    Open in Decisions
                  </Button>
                ) : null}
              </Space>
              <Paragraph
                style={{
                  background: '#0b1021',
                  color: '#dbe4ff',
                  borderRadius: 6,
                  padding: 12,
                  maxHeight: 360,
                  overflow: 'auto',
                  fontFamily: 'ui-monospace,Menlo,Consolas,monospace',
                  fontSize: 12,
                  whiteSpace: 'pre',
                }}
              >
                {payloadString || '(empty)'}
              </Paragraph>
            </div>
          </>
        ) : null}
      </Drawer>
    </div>
  );
};

export default ActivityTab;
